import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Hook central para consultar/aprovar/cancelar atualizações do sistema.
 * Usado por MasterPage (read-only) e LicensePage (com ações).
 */
export function useUpdateStatus({ autoLoad = true } = {}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/updates/status`, { withCredentials: true });
      setStatus(data);
      return data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar status de atualização');
      setStatus(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const forceCheck = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/updates/check`, {}, { withCredentials: true });
      setStatus(data);
      return { ok: true, data };
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao verificar atualização';
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  const approve = useCallback(async (password) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/updates/approve`, { password }, { withCredentials: true });
      setStatus(data);
      return { ok: true, data };
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao aprovar atualização';
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  const cancelApproval = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/updates/cancel-approval`, {}, { withCredentials: true });
      setStatus(data);
      return { ok: true, data };
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erro ao cancelar';
      setError(msg);
      return { ok: false, error: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoLoad) refresh();
  }, [autoLoad, refresh]);

  return { status, loading, error, refresh, forceCheck, approve, cancelApproval };
}
